'use client';

import { useMutation } from '@apollo/client';
import { Carousel } from '@mantine/carousel';
import {
  ActionIcon,
  Box,
  Image as MantineImage,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import DeleteIcon from '@material-design-icons/svg/filled/delete.svg';
import UploadIcon from '@material-design-icons/svg/filled/upload.svg';
import { RefObject, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { MediaUploader, TrackedButton } from '@/components';
import { useIssuePage } from '@/contexts';
import { ProfilePhotoUpload_RemoveProfilePhotoDocument } from '@/graphql';
import { logger } from '@/utils';

import styles from './IssuePageInformationSection.module.css';

interface IssuePageInformationSectionProps {
  formData: {
    summary: string;
    location: string;
    imageUrls?: string[];
  };
  handleFormChange: (field: string, value: string) => void;
  handleIssueImagesUpdate?: (urls: string[]) => void;
  targetRef: RefObject<HTMLDivElement>;
  entityId?: string;
  issueId?: string;
  issueMediaRels?: {
    id: string;
    medium: {
      id: string;
      link: string;
    };
  }[];
}

const IssuePageInformationSection: React.FC<
  IssuePageInformationSectionProps
> = ({
  formData,
  handleFormChange,
  handleIssueImagesUpdate,
  targetRef,
  entityId,
  issueId,
  issueMediaRels = [],
}) => {
  const { getIsStaticMode, getIsFullEditMode } = useIssuePage();
  const [showUploader, setShowUploader] = useState(false);
  const [deleteMedia] = useMutation(
    ProfilePhotoUpload_RemoveProfilePhotoDocument,
  );

  const handleImageUpdates = (urls: string[]) => {
    const imageUrls = Array.isArray(urls) ? urls : [urls];

    // Set first new image as main image if there are any
    if (imageUrls.length > 0) {
      handleFormChange('imageUrl', imageUrls[0]);
    }

    // Add all new URLs to the array if not already present
    const currentUrls = formData.imageUrls || [];
    const newUrls = [...currentUrls];

    // Only add URLs that don't already exist
    imageUrls.forEach((url) => {
      if (!newUrls.includes(url)) {
        newUrls.push(url);
      }
    });

    handleIssueImagesUpdate?.(newUrls);
    setShowUploader(false);
  };

  const handleLocalImageDelete = (index: number) => {
    if (!formData.imageUrls) return;
    const newUrls = [...formData.imageUrls];
    newUrls.splice(index, 1);
    handleIssueImagesUpdate?.(newUrls);
  };

  const handleImageDelete = async (imageUrl: string, index: number) => {
    if (issueId) {
      // Find the media relation for this image URL
      const mediaRel = issueMediaRels.find(
        (rel) => rel.medium.link === imageUrl,
      );
      if (mediaRel) {
        // Delete the relationship in the database
        try {
          await deleteMedia({
            variables: {
              mediaId: mediaRel.medium.id,
            },
          });
        } catch (error) {
          logger.error('Failed to delete image', {
            error: error instanceof Error ? error.message : String(error),
            component: 'IssuePageInformationSection',
          });
        }
      }
    }
    handleLocalImageDelete(index);
  };

  const toggleUploader = () => {
    setShowUploader(!showUploader);
  };

  return (
    <>
      {/* What is section */}
      <Stack className={styles.infoSection}>
        <Title order={2}>Why does this matter?</Title>
        {getIsStaticMode() ? (
          formData.summary ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ ...props }) => (
                  <Title
                    order={3}
                    {...props}
                  />
                ),
                h2: ({ ...props }) => (
                  <Title
                    order={3}
                    {...props}
                  />
                ),
                p: ({ ...props }) => (
                  <Text
                    size="md"
                    {...props}
                  />
                ),
              }}
            >
              {formData.summary}
            </ReactMarkdown>
          ) : (
            <Text
              size="md"
              style={{
                color: 'var(--mantine-color-neutral-5)',
              }}
            >
              No summary provided.
            </Text>
          )
        ) : (
          <Textarea
            value={formData.summary}
            disabled={!getIsFullEditMode()}
            onChange={(e) => handleFormChange('summary', e.target.value)}
            minRows={4}
            autosize
            placeholder="Tell us about your topic and what it means to you ..."
            mt="md"
            styles={{
              input: {
                height: '300px',
              },
            }}
          />
        )}

        {formData.imageUrls && formData.imageUrls.length > 1 ? (
          <Box mt="md">
            <Carousel
              withIndicators={true}
              height={400}
              slideSize="100%"
              slideGap="md"
              controlsOffset="md"
              controlSize={26}
            >
              {formData.imageUrls.map((image, index) => (
                <Carousel.Slide key={index}>
                  <div className={styles.imageSlideContainer}>
                    <MantineImage
                      src={image}
                      height={400}
                      width={600}
                      fit="contain"
                      alt={`Issue image ${index + 1}`}
                    />
                    {getIsFullEditMode() && (
                      <ActionIcon
                        className={styles.deleteButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageDelete(image, index);
                        }}
                        aria-label="Delete image"
                      >
                        <DeleteIcon />
                      </ActionIcon>
                    )}
                  </div>
                </Carousel.Slide>
              ))}
            </Carousel>
            {getIsFullEditMode() && (
              <TrackedButton
                variant="outline"
                onClick={toggleUploader}
                fullWidth
                mt="sm"
                trackingName="add_more_images"
                trackingProperties={{
                  imageCount: formData.imageUrls.length,
                }}
              >
                Add More Images
              </TrackedButton>
            )}
          </Box>
        ) : formData.imageUrls && formData.imageUrls.length === 1 ? (
          <Box mt="md">
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '300px',
                maxHeight: '300px',
                overflow: 'hidden',
              }}
              className={styles.imageSlideContainer}
            >
              <MantineImage
                src={formData.imageUrls[0]}
                alt="Issue image"
                height={300}
                width={600}
                fit="contain"
                style={{ objectFit: 'cover' }}
              />
              {getIsFullEditMode() && (
                <ActionIcon
                  className={styles.deleteButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageDelete(formData.imageUrls![0], 0);
                  }}
                  aria-label="Delete image"
                >
                  <DeleteIcon />
                </ActionIcon>
              )}
            </div>
            {getIsFullEditMode() && (
              <TrackedButton
                variant="outline"
                onClick={toggleUploader}
                mt="sm"
                fullWidth
                trackingName="add_more_images"
                trackingProperties={{
                  imageCount: formData.imageUrls.length,
                }}
              >
                Add More Images
              </TrackedButton>
            )}
          </Box>
        ) : (
          !getIsStaticMode() && (
            <Box
              onClick={getIsFullEditMode() ? toggleUploader : undefined}
              className={`${styles.uploadContainer} ${
                getIsFullEditMode()
                  ? styles.enabledUploadContainer
                  : styles.disabledUploadContainer
              }`}
              role={getIsFullEditMode() ? 'button' : undefined}
            >
              <UploadIcon
                style={{
                  opacity: getIsFullEditMode() ? 1 : 0.2,
                }}
              />
              <Text
                size="sm"
                c={getIsFullEditMode() ? 'neutral.9' : 'neutral.3'}
              >
                Add images to give more context
              </Text>
              <Text
                size="xs"
                c={getIsFullEditMode() ? 'neutral.9' : 'neutral.3'}
              >
                (JPG, PNG - max 10MB, up to 5 images)
              </Text>
            </Box>
          )
        )}
        {showUploader && entityId && (
          <MediaUploader
            entityId={entityId}
            showUploader={showUploader}
            onImageUpdate={handleImageUpdates}
            allowMultiple={true}
          />
        )}
      </Stack>

      <div className={styles.divider} />

      {/* Location section */}
      <Stack
        ref={targetRef}
        className={styles.infoSection}
      >
        <Title order={2}>Where does this matter?</Title>
        {getIsStaticMode() ? (
          <Text
            size="md"
            style={{
              color: formData.location
                ? 'inherit'
                : 'var(--mantine-color-neutral-5)',
            }}
          >
            {formData.location || 'No location provided.'}
          </Text>
        ) : (
          <Textarea
            variant="filled"
            value={formData.location}
            disabled={!getIsFullEditMode()}
            onChange={(e) => handleFormChange('location', e.target.value)}
            placeholder="Tell us which area(s) you'll be focusing your efforts on (e.g., the name of your neighborhood, city, school district, county, or state)..."
            mt="md"
            minRows={4}
            styles={{
              input: {
                height: '120px',
              },
            }}
          />
        )}
      </Stack>
    </>
  );
};

export { IssuePageInformationSection };
